// The felt's hit test — pure geometry, no React, no DOM.
//
// WHY THIS EXISTS AT ALL
// ----------------------
// A roulette felt has three kinds of target stacked on top of each other: the
// CENTRE of a cell (straight up), the LINE between two cells (split / street)
// and the POINT where four meet (corner / six line). The first build modelled
// them as invisible <button>s hanging off each cell — 12x15px and 16x16px.
// On a phone that is not a small target, it is no target: this project's own
// floor is 42px, and those boxes also stole ~26% of the straight-up cell they
// were sitting on. Measured on an iPhone 12 you could effectively only place
// straight-up bets, and half of those landed on the wrong bet.
//
// Boxes cannot be fixed by growing them — three grid lines cannot each be 7mm
// wide inside one cell; that is arithmetic, not effort. So the model changes:
// we resolve a point to the NEAREST FEATURE. The decision boundary becomes the
// target, which means every feature owns a region instead of a box, and the
// live preview (see Overlay) shows what you would get before you commit.
//
// Everything here is a pure function of a measured Lattice, so it is correct at
// any cell size and unit-testable exactly like engine.ts.

import { columnNumbers, dozenNumbers, OUTSIDE, PAYOUT, streetNumbers, type BetKind } from './engine';

/** A bet the felt can produce. `numbers` is the single source of truth for both
 *  what it pays (via COVERED/PAYOUT) and what lights up. */
export type PendingBet = {
  id: string;
  kind: BetKind;
  numbers: number[];
  title: string;      // "Corner 1·2·4·5"
  sub: string;        // "4 numbers · pays 8 to 1"
};

export type Snap = 'centre' | 'vline' | 'hline' | 'point';

/** A cell of the number lattice. r=0 is the ZERO band (one cell spanning all
 *  three columns); r=1..12 are the number rows. */
export type Cell = { r: number; c: number; span?: number };

export type Target = PendingBet & {
  snap: Snap;
  cells: Cell[];
  /** Where the chip physically sits, in lattice-local px. */
  marker: { x: number; y: number };
};

/**
 * The lattice is PURE SIZE. It deliberately carries no screen position.
 *
 * It used to hold `left`/`top` — the client-px origin of the zero band — and
 * resolve() differenced a pointer against them. That cache was refreshed by a
 * ResizeObserver, and a ResizeObserver fires on SIZE and never on POSITION, so
 * the frame silently drifted on a real phone (toolbar collapse, 100dvh
 * re-resolving, env() safe-area landing after first paint) and every number tap
 * was computed against stale coordinates. Desktop never reproduced it because
 * nothing there ever moves.
 *
 * The board now translates for the camera pan, so a stored position would be
 * wrong by design rather than by accident. Positions come from the DOM at the
 * moment of use (document.elementFromPoint + a live getBoundingClientRect) and
 * from nowhere else. Adding a position field back here, or any pan compensation
 * in the placement path, is a defect on sight.
 */
export type Lattice = {
  cellW: number;
  cellH: number;
  gap: number;
};

export const ROWS = 13;   // row 0 = zero band, rows 1..12 = the number rows
export const COLS = 3;

export const pitchX = (L: Lattice) => L.cellW + L.gap;
export const pitchY = (L: Lattice) => L.cellH + L.gap;

/** Board.tsx lays rows out as [1,2,3], [4,5,6] … so row r (1-based), col c (0-based). */
export const numAt = (r: number, c: number) => (r - 1) * 3 + c + 1;

const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));

/** Canonical, order-independent id. Two routes to the same bet MUST produce the
 *  same id or the chip stack silently splits in two. */
export function makeBet(kind: BetKind, numbers: number[], title: string): PendingBet {
  const sorted = [...numbers].sort((a, b) => a - b);
  return { id: `${kind}:${sorted.join('-')}`, kind, numbers: sorted, title, sub: subFor(kind, sorted) };
}

/** The plain-language second line. Deliberately says what it PAYS, not the
 *  ratio-first jargon a first-time player has to decode. */
export function subFor(kind: BetKind, numbers: number[]): string {
  const n = numbers.length;
  return `${n} number${n === 1 ? '' : 's'} · pays ${PAYOUT[kind]} to 1`;
}

/** Total returned (stake + profit) if this bet wins — presentation only. */
export const returnOf = (kind: BetKind, stake: number) => stake * (PAYOUT[kind] + 1);

/* ─── Measurement ─────────────────────────────────────────────────────────
 * cellW/cellH are DERIVED from the container, never read off one cell's rect:
 * a cell's getBoundingClientRect() is device-pixel-rounded, and accumulating
 * that rounding across three columns puts the lattice ~1px out at the right
 * border — which is exactly where street and six line live. */
/**
 * AN ELEMENT'S LAYOUT SIZE — and why this is not getBoundingClientRect().
 *
 * Portrait-only games render inside `.force-portrait`, which on a phone held
 * sideways is `width: 100dvh; height: 100dvw` plus `rotate(-90deg)`. The layout
 * inside it is still portrait and still correct; only the presentation is
 * rotated.
 *
 * `getBoundingClientRect()` reports the element's box AFTER every ancestor
 * transform — so under that rotation it hands back the width and height
 * SWAPPED. On a 390x844 phone turned sideways it reports the canvas as 844 wide
 * instead of 390, and everything derived from it — the wheel diameter, the
 * lattice, the camera throw — comes out 2.16x too large. That is the whole of
 * the "rotating the phone makes everything huge and nothing works" bug.
 *
 * `offsetWidth` / `offsetHeight` are LAYOUT values. No ancestor transform can
 * touch them, which is exactly the property wanted here. They are integers,
 * which costs nothing: every consumer rounds anyway.
 *
 * RULE: anything deriving a SIZE uses this. Anything comparing a POSITION
 * against a pointer event keeps using getBoundingClientRect(), because pointer
 * coordinates are in the same transformed space and the two must agree.
 */
export function layoutBox(el: HTMLElement): { width: number; height: number } {
  return { width: el.offsetWidth, height: el.offsetHeight };
}

/**
 * THE VIEW'S ROTATION — measured, never assumed.
 *
 * Pointer events and getBoundingClientRect both live in VIEWPORT space. In
 * portrait that is also the board's space, so "near the cell's right edge"
 * means "near the line between two side-by-side numbers". But on a phone held
 * landscape, `.force-portrait` rotates the whole game ±90°, and a rotated
 * cell's client rect comes back with its axes swapped: the same edge test now
 * fires on the WRONG BOARD AXIS. The symptom (2026-08-16 audit, C3): aiming at
 * the line between 5 and 6 placed a split on 5-and-8 instead, and a wide slice
 * of every cell resolved as a wrong-axis split on plain taps.
 *
 * Why this reads the COMPUTED TRANSFORM and not the wrapper's data-angle: the
 * attribute reports the device's orientation, but the CSS that actually rotates
 * is gated on `(orientation: landscape) and (max-width: 1023px)` — a desktop in
 * landscape has angle=90 and NO rotation. The computed matrix is the ground
 * truth of what the compositor did; the attribute is what the sensor said.
 */
function viewRotation(): 0 | 90 | 180 | 270 {
  const fp = typeof document !== 'undefined'
    ? (document.querySelector('.force-portrait') as HTMLElement | null) : null;
  if (!fp) return 0;
  const t = getComputedStyle(fp).transform;
  if (!t || t === 'none') return 0;
  const m = new DOMMatrix(t);
  const deg = Math.round(Math.atan2(m.b, m.a) * (180 / Math.PI));
  return (((deg % 360) + 360) % 360) as 0 | 90 | 180 | 270;
}

/**
 * Where a pointer sits WITHIN a cell, in BOARD axes.
 *
 *   u — fraction along the board's x (0 = the cell's board-left edge)
 *   v — fraction along the board's y (0 = the cell's board-top edge)
 *   spanU/spanV — the cell's physical on-screen extent along each board axis,
 *                 so pixel thresholds (`within 12px of the line`) keep meaning
 *                 the same thing at every rotation.
 *
 * The mapping per rotation is the inverse of what CSS did: rotate(-90deg)
 * (wrapper angle 90) sends board +x to viewport −y, so walking UP the screen
 * is walking RIGHT across the board, and so on. Verified against the wrapper's
 * actual transforms in index.css.
 */
export function cellFrac(q: DOMRect, x: number, y: number):
  { u: number; v: number; spanU: number; spanV: number } {
  switch (viewRotation()) {
    case 270:  // CSS rotate(-90deg) — the common landscape
      return { u: (q.bottom - y) / q.height, v: (x - q.left) / q.width,
               spanU: q.height, spanV: q.width };
    case 90:   // CSS rotate(+90deg) — landscape the other way
      return { u: (y - q.top) / q.height, v: (q.right - x) / q.width,
               spanU: q.height, spanV: q.width };
    case 180:
      return { u: (q.right - x) / q.width, v: (q.bottom - y) / q.height,
               spanU: q.width, spanV: q.height };
    default:
      return { u: (x - q.left) / q.width, v: (y - q.top) / q.height,
               spanU: q.width, spanV: q.height };
  }
}

/**
 * A viewport point in the WRAPPER'S local space — for positioning fixed
 * elements that live INSIDE `.force-portrait`.
 *
 * `position: fixed` inside a transformed ancestor anchors to that ancestor,
 * not the viewport, so its left/top are wrapper-local — but pointer events
 * arrive in viewport coordinates. In portrait the two coincide; rotated, a
 * loupe fed raw clientX/Y lands nowhere near the finger (audit C3, second
 * half). The wrapper's full matrix (rotation AND translation) inverts the
 * mapping exactly, and in portrait it is the identity, so this is safe to
 * call unconditionally.
 */
export function toWrapperLocal(x: number, y: number): { x: number; y: number } {
  const fp = typeof document !== 'undefined'
    ? (document.querySelector('.force-portrait') as HTMLElement | null) : null;
  if (!fp) return { x, y };
  const t = getComputedStyle(fp).transform;
  if (!t || t === 'none') return { x, y };
  const p = new DOMMatrix(t).inverse().transformPoint(new DOMPoint(x, y));
  /* Fixed-position children anchor to the containing block's PADDING edge,
     and in rotated modes the wrapper carries the safe-area remap as padding
     (up to ~59px on a notched phone) -- inverting only the matrix left the
     loupe that far from the fingertip on exactly those devices. Portrait
     padding is 0, so this is the identity where it always worked.
     (Recheck P5.) */
  const cs = getComputedStyle(fp);
  return { x: p.x - (parseFloat(cs.paddingLeft) || 0), y: p.y - (parseFloat(cs.paddingTop) || 0) };
}

export function measureLattice(grid: HTMLElement, railL: number, railR: number, gap: number): Lattice {
  const r = layoutBox(grid);
  // The number columns are what is left after BOTH rails. Two arguments, not
  // one: the even-money rail is on the left and the dozen rail is on the right,
  // and collapsing them into a single "railW" is how every chip, stud and
  // highlight would end up offset by 44px with nothing to show for it.
  const numbersW = r.width - railL - railR - 2 * gap;
  return {
    cellW: (numbersW - (COLS - 1) * gap) / COLS,
    // 14 rows in the grid (zero + 12 numbers + the 2:1 strip), 13 gaps.
    cellH: (r.height - 13 * gap) / 14,
    gap,
  };
}

/* ─── Markers: where the chip sits ───────────────────────────────────────── */

const mkCentre = (L: Lattice, r: number, c: number) => ({
  x: r === 0 ? (COLS * L.cellW + (COLS - 1) * L.gap) / 2 : c * pitchX(L) + L.cellW / 2,
  y: r * pitchY(L) + L.cellH / 2,
});
const mkV = (L: Lattice, vi: number, r: number) => ({ x: vi * pitchX(L) - L.gap / 2, y: r * pitchY(L) + L.cellH / 2 });
const mkH = (L: Lattice, c: number, hi: number) => ({ x: c * pitchX(L) + L.cellW / 2, y: hi * pitchY(L) - L.gap / 2 });
const mkP = (L: Lattice, vi: number, hi: number) => ({ x: vi * pitchX(L) - L.gap / 2, y: hi * pitchY(L) - L.gap / 2 });

const T = (b: PendingBet, snap: Snap, cells: Cell[], marker: { x: number; y: number }): Target =>
  ({ ...b, snap, cells, marker });

/* ─── The semantic layer ─────────────────────────────────────────────────── */

function atCentre(L: Lattice, r: number, c: number): Target {
  if (r === 0) return T(makeBet('straight', [0], 'Number 0'), 'centre', [{ r: 0, c: 0, span: 3 }], mkCentre(L, 0, 0));
  const n = numAt(r, c);
  return T(makeBet('straight', [n], `Number ${n}`), 'centre', [{ r, c }], mkCentre(L, r, c));
}

function atVLine(L: Lattice, vi: number, r: number): Target {
  if (r === 0) return atCentre(L, 0, 1);                    // the zero band is ONE cell
  if (vi === COLS) {                                        // outer edge = the whole row
    const st = streetNumbers(r);
    return T(makeBet('street', st, `Row ${st[0]}-${st[2]}`), 'vline',
      [{ r, c: 0 }, { r, c: 1 }, { r, c: 2 }], mkV(L, COLS, r));
  }
  const a = numAt(r, vi - 1), b = numAt(r, vi);
  return T(makeBet('split', [a, b], `Split ${a}/${b}`), 'vline',
    [{ r, c: vi - 1 }, { r, c: vi }], mkV(L, vi, r));
}

function atHLine(L: Lattice, c: number, hi: number): Target {
  if (hi === 1) {                                           // the 0 | 1-2-3 line
    const b = numAt(1, c);
    return T(makeBet('split', [0, b], `Split 0/${b}`), 'hline',
      [{ r: 0, c: 0, span: 3 }, { r: 1, c }], mkH(L, c, 1));
  }
  const a = numAt(hi - 1, c), b = numAt(hi, c);
  return T(makeBet('split', [a, b], `Split ${a}/${b}`), 'hline',
    [{ r: hi - 1, c }, { r: hi, c }], mkH(L, c, hi));
}

function atPoint(L: Lattice, vi: number, hi: number): Target {
  if (hi === 1) {
    // Lattice points along the zero line. These three bets exist on every real
    // European felt and the first build had none of them — a player who fancied
    // zero could only take it straight up at 35:1, with no way to cover it
    // cheaply and no way to hedge an inside bet against it. They need no engine
    // change: payout = 36/covered − 1 already gives trio 11:1 and first-four 8:1.
    if (vi === COLS) {
      return T(makeBet('corner', [0, 1, 2, 3], 'First four 0·1·2·3'), 'point',
        [{ r: 0, c: 0, span: 3 }, { r: 1, c: 0 }, { r: 1, c: 1 }, { r: 1, c: 2 }], mkP(L, COLS, 1));
    }
    const a = numAt(1, vi - 1), b = numAt(1, vi);
    return T(makeBet('street', [0, a, b], `Trio 0·${a}·${b}`), 'point',
      [{ r: 0, c: 0, span: 3 }, { r: 1, c: vi - 1 }, { r: 1, c: vi }], mkP(L, vi, 1));
  }
  if (vi === COLS) {                                        // outer edge point = six line
    const six = [...streetNumbers(hi - 1), ...streetNumbers(hi)];
    return T(makeBet('sixline', six, `Six line ${six[0]}-${six[5]}`), 'point',
      [0, 1, 2].flatMap((c) => [{ r: hi - 1, c }, { r: hi, c }]), mkP(L, COLS, hi));
  }
  const four = [numAt(hi - 1, vi - 1), numAt(hi - 1, vi), numAt(hi, vi - 1), numAt(hi, vi)];
  return T(makeBet('corner', four, `Corner ${[...four].sort((a, b) => a - b).join('·')}`), 'point',
    [{ r: hi - 1, c: vi - 1 }, { r: hi - 1, c: vi }, { r: hi, c: vi - 1 }, { r: hi, c: vi }], mkP(L, vi, hi));
}

/* ─── Point → bet ────────────────────────────────────────────────────────
 * There is no point-to-bet resolver here any more. There used to be one —
 * resolve(L, px, py, …) — which differenced a client point against the
 * lattice's cached origin. It was already dead code (nothing imported it) but
 * it read as usable, and under the camera pan it would have been permanently,
 * invisibly wrong.
 *
 * The live path is targetAtCell() at the bottom of this file: the CELL comes
 * from document.elementFromPoint, so the browser decides what is under the
 * finger, and the lattice is used only to position the chip marker. If the
 * lattice is a pixel stale the chip is drawn a pixel off; the BET is still
 * right. That asymmetry is the whole design. */

/** Every bet that touches a given cell — powers the long-press "All bets here"
 *  sheet, which is how a corner or a six line becomes a 48px row instead of a
 *  4mm point, and the keyboard/screen-reader path at the same time. */
export function betsAtCell(L: Lattice, r: number, c: number): Target[] {
  const out: Target[] = [];
  const seen = new Set<string>();
  const push = (t: Target | null) => { if (t && !seen.has(t.id)) { seen.add(t.id); out.push(t); } };
  if (r === 0) {
    push(atCentre(L, 0, 0));
    for (let cc = 0; cc < COLS; cc++) push(atHLine(L, cc, 1));
    push(atPoint(L, 1, 1)); push(atPoint(L, 2, 1)); push(atPoint(L, COLS, 1));
    return out;
  }
  push(atCentre(L, r, c));
  for (const vi of [c, c + 1]) if (vi >= 1 && vi <= COLS) push(atVLine(L, vi, r));
  for (const hi of [r, r + 1]) if (hi >= 1 && hi <= 12) push(atHLine(L, c, hi));
  for (const vi of [c, c + 1]) for (const hi of [r, r + 1]) {
    if (vi >= 1 && vi <= COLS && hi >= 1 && hi <= 12) push(atPoint(L, vi, hi));
  }
  return out;
}

/* ─── The outside bets ───────────────────────────────────────────────────
 * These live here rather than in Board.tsx because the winning-zone highlight
 * has to derive from them, and Board.tsx already imports this file. Defining
 * them there and importing them back would be a cycle. They are pure makeBet
 * calls with no JSX, so this is their natural home anyway. */

export const dozenBet = (d: 1 | 2 | 3) =>
  makeBet('dozen', dozenNumbers(d), ['First row 1st 12', 'Second row 2nd 12', 'Third row 3rd 12'][d - 1]);

export const columnBet = (c: 1 | 2 | 3) =>
  makeBet('column', columnNumbers(c), `${['First', 'Second', 'Third'][c - 1]} game 2~1`);

/** Top-to-bottom in the order the left rail shows them. */
export const EVEN_BETS: PendingBet[] = [
  makeBet('low', OUTSIDE.low, '1~18 Small'),
  makeBet('even', OUTSIDE.even, 'Even'),
  makeBet('red', OUTSIDE.red, 'Red'),
  makeBet('black', OUTSIDE.black, 'Black'),
  makeBet('odd', OUTSIDE.odd, 'Odd'),
  makeBet('high', OUTSIDE.high, '19~36 Big'),
];

/** Every outside bet, in one array — the source the highlight iterates. */
export const OUTSIDE_BETS: PendingBet[] = [
  ...EVEN_BETS,
  dozenBet(1), dozenBet(2), dozenBet(3),
  columnBet(1), columnBet(2), columnBet(3),
];

/**
 * Everything on the board that WINS on `n`, as the ids of the outside bets plus
 * the number's own cell.
 *
 * Derived from `bet.numbers` — the same array that decides what actually pays —
 * so the lights can never disagree with the money. Note 0: it wins no outside
 * bet at all (OUTSIDE.even is built from 1..36, because JavaScript says 0 is
 * even and the felt says 0 is nobody's), so the id set comes back empty and
 * only the zero pill lights.
 */
export function winningZones(n: number): { cells: Cell[]; ids: Set<string> } {
  return {
    cells: cellsForNumbers([n]),
    ids: new Set(OUTSIDE_BETS.filter((b) => b.numbers.includes(n)).map((b) => b.id)),
  };
}

/** Cell → rect in lattice-local px, for the highlight overlay. */
export function cellRect(L: Lattice, cell: Cell) {
  const span = cell.span ?? 1;
  return {
    x: cell.c * pitchX(L),
    y: cell.r * pitchY(L),
    width: span * L.cellW + (span - 1) * L.gap,
    height: L.cellH,
  };
}

/** Board cells covered by a set of numbers. THE single coverage function: the
 *  highlight, the winner reveal and the outside previews all derive from
 *  `bet.numbers`, so the map can never drift away from what actually pays.
 *
 *  Note 0 lights ONLY when 0 is in the set. The classic bug here is building an
 *  "EVEN" preview from `n % 2 === 0` over 0..36 — JavaScript says 0 is even, the
 *  felt says 0 is nobody's. OUTSIDE.even is built from 1..36 and is correct. */
export function cellsForNumbers(numbers: number[]): Cell[] {
  const out: Cell[] = [];
  for (const n of numbers) {
    if (n === 0) out.push({ r: 0, c: 0, span: COLS });
    else out.push({ r: Math.floor((n - 1) / 3) + 1, c: (n - 1) % 3 });
  }
  return out;
}

/** Every point on the felt where a chip may be placed on a LINE — the edge
 *  midpoints (splits, streets) and the vertices (corners, trios, six lines,
 *  first four). Drawn as small studs on the gridlines the moment a token is
 *  picked up, so the player can SEE where the line bets live instead of having
 *  to know that roulette has them. Cell centres are deliberately absent: the
 *  number itself is already the affordance for a straight-up bet. */
export function linePoints(L: Lattice): { x: number; y: number; k: 'v' | 'h' | 'p' }[] {
  const out: { x: number; y: number; k: 'v' | 'h' | 'p' }[] = [];
  const PX = pitchX(L), PY = pitchY(L);
  // INTERIOR LINES ONLY.
  //
  // vi stops at COLS-1 and hi starts at 2, which drops two sets of studs that
  // were being drawn where there is nothing to bet BETWEEN:
  //   * vi = COLS put 24 studs 2px OUTSIDE the last number column — a column of
  //     dots floating on the felt border with no cell to their right.
  //   * hi = 1 put 6 studs in the 3px gap under the 0 bar.
  // Both read as stray marks rather than targets. The bets they stood for
  // (street, six line, the zero splits / trios / first four) are still offered
  // by the long-press "All bets here" sheet, where they appear as named rows
  // instead of unexplained dots.
  for (let r = 1; r <= 12; r++) {
    for (let vi = 1; vi < COLS; vi++) {
      out.push({ x: vi * PX - L.gap / 2, y: r * PY + L.cellH / 2, k: 'v' });
    }
  }
  for (let hi = 2; hi <= 12; hi++) {
    for (let c = 0; c < COLS; c++) {
      out.push({ x: c * PX + L.cellW / 2, y: hi * PY - L.gap / 2, k: 'h' });
    }
    for (let vi = 1; vi < COLS; vi++) {
      out.push({ x: vi * PX - L.gap / 2, y: hi * PY - L.gap / 2, k: 'p' });
    }
  }
  return out;
}

/** Build the bet for a cell the DOM has already identified, plus which of its
 *  edges / corners the finger is nearest.
 *
 *  THIS IS THE IMPORTANT ONE. The caller finds the cell with
 *  document.elementFromPoint(), so the CELL is whatever the browser says is
 *  under the finger — it can never drift. The Lattice is used only to position
 *  the chip marker, which is cosmetic: if it is a pixel stale the chip is drawn
 *  a pixel off, but the BET is still right.
 *
 *  The previous design resolved the whole thing from a cached copy of the
 *  grid's screen rect. That cache was refreshed by a ResizeObserver, which
 *  fires on SIZE changes and never on POSITION changes — so on a real phone
 *  (toolbar collapsing, 100dvh re-resolving, env() safe-area values landing
 *  after first paint) the frame silently shifted and every number tap was
 *  computed against the wrong coordinates. Desktop never reproduced it because
 *  nothing there ever moves. The outside bets were unaffected because they were
 *  always resolved through the DOM. */
export function targetAtCell(
  L: Lattice, r: number, c: number, vi: number | null, hi: number | null,
): Target {
  if (vi != null && hi != null) return atPoint(L, vi, hi);
  if (vi != null) return atVLine(L, vi, r);
  if (hi != null) return atHLine(L, c, hi);
  return atCentre(L, r, c);
}
